import React, { useRef, useCallback, useEffect, useState, useMemo } from 'react';
import axios from 'axios';
import {
  Car,
  MoreVertical,
  RefreshCw,
  X,
  CheckCircle, 
  XCircle, 
  AlertTriangle, 
  Loader2, 
  Filter,
  ArrowUpDown,
  ChevronDown,
  ChevronUp,
  Download,
  Users,
  Minimize2,
  Maximize2,
  Clock,
  Columns,
  Eye,
  EyeOff,
  Cpu,
  Camera,
  ArrowRightLeft,
  Activity,
  Tag
} from 'lucide-react';
import { Button } from './ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Badge } from './ui/badge';
import { VIN_SUMMARY_FIELDS } from './vinSummaryFields';
import { downloadSheet } from '../lib/exportSheet';
import { useAuth } from '../contexts/AuthContext';

// Columns kept in the compact view. Status is deliberately first — it is the first
// column in the table. Image version is advanced-only: the LTA version derived from it
// is what operators read, so showing both here is noise.
const COMPACT_COLUMN_KEYS = [
  'status',
  'serialNumber',
  'azureStatus',
  'upgradeStatus',
  'configStatus',
  'ltaVersion',
  'lastUpdate',
  'busActive',
  'busSpeed'
];

// Onboarding status vocabulary — mirrors webapp/services/onboarding_status.py.
const STATUS_ONBOARDED = 'onboarded';
const STATUS_PENDING_UPGRADE = 'pending_upgrade';
const STATUS_NO_CAN_DATA = 'no_can_data';
const STATUS_AWAITING_DATA = 'awaiting_data';
const STATUS_INSTALLATION_REVIEW = 'installation_review';
const STATUS_OFFLINE = 'offline';

const STATUS_META = {
  [STATUS_ONBOARDED]: {
    label: 'Onboarded',
    badgeClass: 'bg-green-500 text-white border-transparent',
    rowClass: 'bg-green-50/30',
    dotClass: 'bg-green-500',
    chartColor: '#10b981'
  },
  [STATUS_PENDING_UPGRADE]: {
    label: 'Pending Upgrade',
    badgeClass: 'bg-blue-50 text-blue-700 border-blue-300',
    rowClass: 'bg-blue-50/30',
    dotClass: 'bg-blue-500',
    chartColor: '#3b82f6'
  },
  [STATUS_NO_CAN_DATA]: {
    label: 'No CAN Data',
    badgeClass: 'bg-orange-50 text-orange-700 border-orange-300',
    rowClass: 'bg-orange-50/30',
    dotClass: 'bg-orange-500',
    chartColor: '#f97316'
  },
  [STATUS_AWAITING_DATA]: {
    label: 'Awaiting Data',
    badgeClass: 'bg-slate-50 text-slate-600 border-slate-300',
    rowClass: 'bg-slate-50/40',
    dotClass: 'bg-gray-500',
    chartColor: '#6b7280'
  },
  [STATUS_INSTALLATION_REVIEW]: {
    label: 'Installation Review',
    badgeClass: 'bg-amber-50 text-amber-700 border-amber-300',
    rowClass: 'bg-amber-50/30',
    dotClass: 'bg-amber-500',
    chartColor: '#f59e0b'
  },
  [STATUS_OFFLINE]: {
    label: 'Offline',
    badgeClass: 'bg-red-50 text-red-700 border-red-300',
    rowClass: 'bg-red-50/30',
    dotClass: 'bg-red-500',
    chartColor: '#ef4444'
  }
};

const STATUS_ORDER = [
  STATUS_ONBOARDED,
  STATUS_PENDING_UPGRADE,
  STATUS_NO_CAN_DATA,
  STATUS_AWAITING_DATA,
  STATUS_INSTALLATION_REVIEW,
  STATUS_OFFLINE
];

// Rows written before the v2 status migration may still carry the old vocabulary.
const LEGACY_STATUS_MAP = {
  complete: STATUS_ONBOARDED,
  incomplete: STATUS_AWAITING_DATA,
  deactivated: STATUS_OFFLINE
};

const normalizeStatus = (result) => {
  const raw = result?.status;
  if (!raw) return result?.is_complete ? STATUS_ONBOARDED : STATUS_AWAITING_DATA;
  return LEGACY_STATUS_MAP[raw] || raw;
};

const statusMeta = (status) => STATUS_META[status] || {
  label: status || 'Unknown',
  badgeClass: 'bg-gray-50 text-gray-600 border-gray-300',
  rowClass: '',
  dotClass: 'bg-gray-400',
  chartColor: '#9ca3af'
};

// CAN status values stored in bus_active (see onboarding_status.py CAN_* constants).
// "Healthy" means the bus delivered data after installation, which is what
// vehicleBusStatus.hasReceivedAfterInstallation proves.
const CAN_HEALTHY = 'healthy';
const CAN_NO_DATA = 'no_data';

// Values that mean a healthy bus, including the pre-v2 raw busActive flag in case a row
// predates the migration.
const CAN_HEALTHY_VALUES = new Set([CAN_HEALTHY, 'active']);

const CAN_LABELS = {
  [CAN_HEALTHY]: 'Healthy',
  [CAN_NO_DATA]: 'No Data',
  active: 'Healthy',
  inactive: 'No Data'
};

const isCanHealthy = (result) => {
  if (Array.isArray(result?.can_signals_received)) {
    return result.can_signals_received.length > 0;
  }
  return CAN_HEALTHY_VALUES.has(String(result?.bus_active || '').toLowerCase());
};

// How often the page re-reads the table the background scanner writes to.
const POLL_INTERVAL_MS = 30000;
// While a pass is running the scanner's own state is polled far more often — at 30s a
// short scan could start and finish between two polls, so the progress was never seen.
const ACTIVE_POLL_INTERVAL_MS = 3000;

// Labels for the phases a pass goes through (webapp/models/onboarding_scan_run.py).
// The preamble — lock wait, device selection, the batched FTCloud task-status call —
// happens before the first device, so "0 / 0 scanned" is all the counters can say there.
const SCAN_PHASE_LABELS = {
  starting: 'Starting scan',
  waiting_for_lock: 'Waiting for another scan to finish',
  selecting: 'Selecting devices',
  loading_tasks: 'Loading upgrade task statuses',
  scanning: 'Checking devices',
  done: 'Finishing up'
};

const formatDuration = (seconds) => {
  if (typeof seconds !== 'number' || seconds < 0) return null;
  if (seconds < 60) return `${Math.round(seconds)}s`;
  const minutes = Math.floor(seconds / 60);
  if (minutes < 60) return `${minutes}m ${Math.round(seconds % 60)}s`;
  return `${Math.floor(minutes / 60)}h ${minutes % 60}m`;
};

// Flattens a maintenance-task/status API response into a flat list of task entries,
// regardless of how deeply the FTCloud payload nests them.
const extractStatusEntries = (payload) => {
  if (!payload) return [];

  const queue = [payload];
  const visited = new Set();
  const entryMap = new Map();
  const candidateKeys = ['data', 'records', 'items', 'list', 'result', 'content'];

  const pushEntry = (entry) => {
    if (!entry || typeof entry !== 'object') return;
    const key = entry.maintTaskId ?? entry.taskId ?? entry.id;
    if (key == null) return;
    const normalizedKey = String(key);
    entryMap.set(normalizedKey, { ...entryMap.get(normalizedKey), ...entry });
  };

  while (queue.length > 0) {
    const current = queue.shift();
    if (current == null || visited.has(current)) {
      continue;
    }
    visited.add(current);

    if (Array.isArray(current)) {
      current.forEach(item => {
        if (item && typeof item === 'object') {
          pushEntry(item);
          queue.push(item);
        }
      });
      continue;
    }

    if (typeof current === 'object') {
      pushEntry(current);
      candidateKeys.forEach(key => {
        if (current[key] !== undefined) {
          queue.push(current[key]);
        }
      });
    }
  }

  return Array.from(entryMap.values());
};

// Live progress of the scan pass the backend is running right now.
//
// The bar is indeterminate until the pass knows how many devices it has to visit: the
// lock wait, the device selection and the batched FTCloud task-status call all happen
// before the first device, and reporting "0 / 0" through them looked like a stuck scan.
// A pass's trigger is "manual:incomplete:my-fleet" / "scheduled" / "enrollment"; the
// operator only needs the part that says whose work this is.
const scanRunLabel = (trigger) => {
  if (!trigger) return 'Scan';
  const [kind, scope, ...fleet] = trigger.split(':');
  if (kind === 'scheduled') return 'Nightly scan';
  if (kind === 'enrollment') return 'New devices';
  const name = fleet.join(':');
  if (scope === 'selection') return 'Selected devices';
  const what = scope === 'all' ? 'All' : 'Incomplete';
  return name ? `${what} · ${name}` : `${what} · all fleets`;
};

// Several passes run at once now, so each gets a narrow card and they sit in a row that
// wraps. One full-width bar per pass would push the table off the screen as soon as two
// people were working.
const LiveScans = ({ runs }) => {
  if (!runs || runs.length === 0) return null;

  return (
    <div className="mt-3 flex flex-wrap gap-2" role="status" aria-live="polite">
      {runs.map(run => {
        const determinate = typeof run.percent === 'number' && run.total > 0;
        const eta = formatDuration(run.eta_seconds);
        return (
          <div
            key={run.id || run.trigger}
            className="flex-1 min-w-[200px] max-w-[320px] px-3 py-2 rounded-lg border border-blue-200 bg-blue-50/60"
            title={`${run.trigger} — ${SCAN_PHASE_LABELS[run.phase] || 'Checking devices'}`}
          >
            <div className="flex items-center justify-between gap-2 text-xs">
              <span className="flex items-center gap-1.5 font-medium text-blue-800 truncate">
                <Loader2 className="w-3 h-3 animate-spin flex-shrink-0" />
                <span className="truncate">{scanRunLabel(run.trigger)}</span>
              </span>
              <span className="text-blue-800 tabular-nums flex-shrink-0">
                {determinate ? `${run.scanned}/${run.total}` : '…'}
              </span>
            </div>

            <div className="mt-1.5 h-1.5 w-full rounded-full bg-blue-100 overflow-hidden">
              {determinate ? (
                <div
                  className="h-full bg-blue-600 transition-all duration-500"
                  style={{ width: `${Math.min(100, Math.max(0, run.percent))}%` }}
                />
              ) : (
                <div className="h-full w-1/3 bg-blue-400 animate-pulse" />
              )}
            </div>

            <div className="mt-1 flex items-center gap-2 text-[11px] text-blue-900/70">
              <span className="truncate">
                {SCAN_PHASE_LABELS[run.phase] || 'Checking devices'}
              </span>
              {run.errors > 0 && (
                <span className="flex items-center gap-0.5 text-amber-700 flex-shrink-0">
                  <AlertTriangle className="w-3 h-3" />
                  {run.errors}
                </span>
              )}
              {eta && <span className="flex-shrink-0">~{eta}</span>}
            </div>
          </div>
        );
      })}
    </div>
  );
};

const ScanProgress = ({ lastRun }) => {
  const { phase, scanned = 0, total = 0, updated = 0, errors = 0, percent, eta_seconds } = lastRun || {};
  const label = SCAN_PHASE_LABELS[phase] || 'Checking devices';
  const determinate = typeof percent === 'number' && total > 0;
  const eta = formatDuration(eta_seconds);

  return (
    <div
      className="mt-3 p-3 rounded-xl border border-blue-200 bg-blue-50/60"
      role="status"
      aria-live="polite"
    >
      <div className="flex items-center justify-between gap-3 text-sm">
        <span className="flex items-center gap-2 font-medium text-blue-800">
          <Loader2 className="w-4 h-4 animate-spin" />
          {label}
        </span>
        <span className="text-blue-800 tabular-nums">
          {determinate ? `${scanned} / ${total} (${percent}%)` : 'Preparing…'}
        </span>
      </div>

      <div className="mt-2 h-2 w-full rounded-full bg-blue-100 overflow-hidden">
        {determinate ? (
          <div
            className="h-full bg-blue-600 transition-all duration-500"
            style={{ width: `${Math.min(100, Math.max(0, percent))}%` }}
          />
        ) : (
          <div className="h-full w-1/3 bg-blue-400 animate-pulse" />
        )}
      </div>

      <div className="mt-2 flex flex-wrap items-center gap-x-4 gap-y-1 text-xs text-blue-900/80">
        <span>{updated} device(s) updated</span>
        {errors > 0 && (
          <span className="flex items-center gap-1 text-amber-700">
            <AlertTriangle className="w-3 h-3" />
            {errors} error(s)
          </span>
        )}
        {eta && <span>~{eta} remaining</span>}
      </div>
    </div>
  );
};

function OnboardingStatus({ devices = [], state, setState }) {
  const { isViewer, isAdmin } = useAuth();
  // Use state from parent to persist across tab changes
  const {
    results = [],
    loading = false,
    error = null,
    filters: rawFilters = {},
    sortConfig = { key: null, direction: 'asc' },
    showFilters = false,
    selectedGroup = 'all-groups', // 'local' for current devices, 'all-groups' or specific group name
    availableGroups = [],
    compactView = true,
    lastUpdated = null,
    visibleColumns: rawVisibleColumns = {}
  } = state || {};

  // Normalize filters to ensure new fields exist (migration from old state)
  const filters = {
    serialNumber: rawFilters.serialNumber || '',
    azureStatus: rawFilters.azureStatus || 'all',
    completionStatus: rawFilters.completionStatus || 'all',
    busActive: rawFilters.busActive || 'all',
    busSpeed: rawFilters.busSpeed || 'all',
    imageVersion: rawFilters.imageVersion || 'all'
  };

  // Normalize visibleColumns to ensure all fields exist with defaults
  const visibleColumns = {
    status: rawVisibleColumns.status !== undefined ? rawVisibleColumns.status : true,
    serialNumber: rawVisibleColumns.serialNumber !== undefined ? rawVisibleColumns.serialNumber : true,
    azureStatus: rawVisibleColumns.azureStatus !== undefined ? rawVisibleColumns.azureStatus : true,
    deviceType: rawVisibleColumns.deviceType !== undefined ? rawVisibleColumns.deviceType : true,
    upgradeType: rawVisibleColumns.upgradeType !== undefined ? rawVisibleColumns.upgradeType : true,
    upgradeStatus: rawVisibleColumns.upgradeStatus !== undefined ? rawVisibleColumns.upgradeStatus : true,
    configStatus: rawVisibleColumns.configStatus !== undefined ? rawVisibleColumns.configStatus : true,
    ltaVersion: rawVisibleColumns.ltaVersion !== undefined ? rawVisibleColumns.ltaVersion : true,
    imageVersion: rawVisibleColumns.imageVersion !== undefined ? rawVisibleColumns.imageVersion : true,
    baseImage: rawVisibleColumns.baseImage !== undefined ? rawVisibleColumns.baseImage : true,
    lastUpdate: rawVisibleColumns.lastUpdate !== undefined ? rawVisibleColumns.lastUpdate : true,
    imei: rawVisibleColumns.imei !== undefined ? rawVisibleColumns.imei : true,
    iccid: rawVisibleColumns.iccid !== undefined ? rawVisibleColumns.iccid : true,
    vin: rawVisibleColumns.vin !== undefined ? rawVisibleColumns.vin : true,
    ipcVersion: rawVisibleColumns.ipcVersion !== undefined ? rawVisibleColumns.ipcVersion : true,
    powerPackVersion: rawVisibleColumns.powerPackVersion !== undefined ? rawVisibleColumns.powerPackVersion : true,
    busActive: rawVisibleColumns.busActive !== undefined ? rawVisibleColumns.busActive : true,
    busSpeed: rawVisibleColumns.busSpeed !== undefined ? rawVisibleColumns.busSpeed : true,
    // Advanced view only — omitted from COMPACT_COLUMN_KEYS.
    canSignals: rawVisibleColumns.canSignals !== undefined ? rawVisibleColumns.canSignals : true
  };

  const effectiveColumns = useMemo(() => {
    if (!compactView) {
      return visibleColumns;
    }

    return Object.keys(visibleColumns).reduce((acc, key) => {
      acc[key] = COMPACT_COLUMN_KEYS.includes(key) && visibleColumns[key];
      return acc;
    }, {});
  }, [compactView, visibleColumns]);

  // Local state for loading groups
  const [loadingGroups, setLoadingGroups] = useState(false);
  const [groupDevices, setGroupDevices] = useState([]);
  const [upgradeStatusMap, setUpgradeStatusMap] = useState({}); // serial -> { upgradeStatus, configStatus }
  const [showColumnMenu, setShowColumnMenu] = useState(false); // Column visibility menu
  const [scanStatus, setScanStatus] = useState(null); // Background scanner state
  const [scanRequested, setScanRequested] = useState(false); // Manual scan in flight
  const [scanMenuOpen, setScanMenuOpen] = useState(false); // Admin scan actions menu
  const scanMenuRef = useRef(null);
  
  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 15;

  // Modal state: CAN signal list, and the VIN decode result
  const [canSignalsModal, setCanSignalsModal] = useState(null); // { serial, signals }
  const [vinModal, setVinModal] = useState(null); // { serial, vin, loading, error, data }

  // Fleet search state
  const [fleetSearchTerm, setFleetSearchTerm] = useState('');
  const [fleetDropdownOpen, setFleetDropdownOpen] = useState(false);
  const fleetDropdownRef = useRef(null);
  const fleetSearchInputRef = useRef(null);

  const [retryingDevice, setRetryingDevice] = useState(null);


  // Track if initial groups load has been done
  const groupsLoadedRef = useRef(false);

  // Derive device type from serial prefix, matching MigrationPanel's convention
  const deviceTypeFromSerial = (serial) => {
    const s = String(serial || '').toUpperCase();
    if (s.startsWith('00D2')) return 'Dashcam';
    if (s.startsWith('0071')) return 'M1N';
    return null;
  };

  // Build upgrade info per device: type/video-solution come from the DB-backed
  // device group record; upgrade/config status come live from the Maintenance API
  // (upgradeStatusMap) and are blank when the device has no task at all.
  const upgradeInfoMap = useMemo(() => {
    const map = {};

    groupDevices.forEach(device => {
      const serial = device.serial;
      if (!serial) return;
      const liveStatus = upgradeStatusMap[serial] || {};
      map[serial] = {
        deviceType: deviceTypeFromSerial(serial),
        upgradeType: device.video_solution || null,
        upgradeStatus: device.upgrade_task_id ? (liveStatus.upgradeStatus || null) : null,
        configStatus: device.config_task_id ? (liveStatus.configStatus || null) : null
      };
    });

    return map;
  }, [groupDevices, upgradeStatusMap]);

  // Fetch live upgrade/config status from the FTCloud Maintenance API for every
  // device that has a task ID. Devices without a task ID are left out of the
  // map entirely, so upgradeInfoMap reports them as blank rather than defaulting.
  const refreshUpgradeStatus = useCallback(async () => {
    const devicesWithUpgradeTask = groupDevices.filter(d => d.upgrade_task_id);
    const devicesWithConfigTask = groupDevices.filter(d => d.config_task_id);
    if (devicesWithUpgradeTask.length === 0 && devicesWithConfigTask.length === 0) {
      setUpgradeStatusMap({});
      return;
    }

    try {
      const nextMap = {};

      if (devicesWithUpgradeTask.length > 0) {
        const taskIds = Array.from(new Set(devicesWithUpgradeTask.map(d => d.upgrade_task_id)));
        const response = await axios.post('/api/ftcloud/maintenance-task/status', { task_ids: taskIds });
        if (response.data.success) {
          const entries = extractStatusEntries(response.data);
          devicesWithUpgradeTask.forEach(device => {
            const entry = entries.find(e =>
              String(e.maintTaskId) === String(device.upgrade_task_id) ||
              String(e.taskId) === String(device.upgrade_task_id) ||
              String(e.id) === String(device.upgrade_task_id)
            );
            if (entry) {
              nextMap[device.serial] = {
                ...nextMap[device.serial],
                upgradeStatus: entry.status || entry.taskStatus || entry.maintStatus || null
              };
            }
          });
        }
      }

      if (devicesWithConfigTask.length > 0) {
        const configTaskIds = Array.from(new Set(devicesWithConfigTask.map(d => d.config_task_id)));
        const configResponse = await axios.post('/api/ftcloud/maintenance-task/status', { task_ids: configTaskIds });
        if (configResponse.data.success) {
          const configEntries = extractStatusEntries(configResponse.data);
          devicesWithConfigTask.forEach(device => {
            const entry = configEntries.find(e =>
              String(e.maintTaskId) === String(device.config_task_id) ||
              String(e.taskId) === String(device.config_task_id) ||
              String(e.id) === String(device.config_task_id)
            );
            if (entry) {
              nextMap[device.serial] = {
                ...nextMap[device.serial],
                configStatus: entry.status || entry.taskStatus || entry.maintStatus || null
              };
            }
          });
        }
      }

      setUpgradeStatusMap(nextMap);
    } catch (err) {
      console.error('Failed to refresh upgrade/config status:', err);
    }
  }, [groupDevices]);

  // Refresh live status whenever the device group (and its task IDs) changes
  useEffect(() => {
    refreshUpgradeStatus();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [groupDevices]);

  // Helper to get upgrade status badge class
  const getUpgradeStatusBadgeClass = (status) => {
    const s = (status || '').toUpperCase();
    if (s === 'SUCCESS' || s === 'COMPLETED') {
      return 'bg-green-100 text-green-700 border-green-300';
    }
    if (s === 'FAILURE' || s === 'ERROR') {
      return 'bg-red-100 text-red-700 border-red-300';
    }
    if (s === 'PENDING' || s === 'WAITING') {
      return 'bg-yellow-100 text-yellow-700 border-yellow-300';
    }
    if (s === 'RUNNING' || s === 'IN_PROGRESS' || s === 'UPGRADING') {
      return 'bg-blue-100 text-blue-700 border-blue-300';
    }
    return 'bg-gray-100 text-gray-700 border-gray-300';
  };

  // Helper to update state
  const updateState = useCallback((updates) => {
    setState(prev => ({ ...prev, ...updates }));
  }, [setState]);

  // Load available groups on mount (only once)
  useEffect(() => {
    if (groupsLoadedRef.current) return;
    groupsLoadedRef.current = true;
    
    const fetchGroups = async () => {
      setLoadingGroups(true);
      try {
        const response = await axios.get('/api/device-groups');
        setState(prev => ({ ...prev, availableGroups: response.data.groups || [] }));
      } catch (err) {
        console.error('Failed to load groups:', err);
      } finally {
        setLoadingGroups(false);
      }
    };
    fetchGroups();
  }, [setState]);

  // Track the last loaded group to avoid reloading on tab switch
  const lastLoadedGroupRef = useRef(null);
  const loadingGroupRef = useRef(false);

  const setResults = useCallback((updater) => {
    setState(prev => ({
      ...prev,
      results: typeof updater === 'function' ? updater(prev.results) : updater
    }));
  }, [setState]);

  const setLoading = useCallback((val) => updateState({ loading: val }), [updateState]);
  const setError = useCallback((val) => updateState({ error: val }), [updateState]);
  const setShowFilters = useCallback((val) => updateState({ showFilters: val }), [updateState]);
  const setFilters = useCallback((updater) => {
    setState(prev => ({
      ...prev,
      filters: typeof updater === 'function' ? updater(prev.filters) : updater
    }));
  }, [setState]);
  const setSortConfig = useCallback((updater) => {
    setState(prev => ({
      ...prev,
      sortConfig: typeof updater === 'function' ? updater(prev.sortConfig) : updater
    }));
  }, [setState]);
  const setCompactView = useCallback((val) => updateState({ compactView: val }), [updateState]);

  const setSelectedGroup = useCallback((val) => {
    // Reset the last loaded group ref so the useEffect will reload data
    lastLoadedGroupRef.current = null;
    setState(prev => ({ ...prev, selectedGroup: val, results: [] })); // Clear results when changing group
  }, [setState]);

  const toggleColumn = useCallback((columnKey) => {
    setState(prev => {
      const currentVisibleColumns = prev.visibleColumns || {};
      return {
        ...prev,
        visibleColumns: {
          ...currentVisibleColumns,
          [columnKey]: !currentVisibleColumns[columnKey]
        }
      };
    });
  }, [setState]);

  const dedupeBy = (items, keyOf) => {
    const seen = new Set();
    return items.filter(item => {
      const key = keyOf(item);
      if (seen.has(key)) return false;
      seen.add(key);
      return true;
    });
  };

  // The group names whose onboarding results make up the current view.
  const selectedGroupNames = useMemo(() => {
    if (selectedGroup === 'local') return [];
    if (selectedGroup === 'all-groups') return availableGroups.map(g => g.name);
    return [selectedGroup];
  }, [selectedGroup, availableGroups]);

  // Read the onboarding results the background scanner writes to the devices table.
  // This is the only source of table data now — the page never scans on its own.
  const fetchResults = useCallback(async (groupNames) => {
    if (groupNames.length === 0) return { results: [], lastUpdated: null };

    const responses = await Promise.all(
      groupNames.map(name => axios.get(`/api/onboarding/results/${encodeURIComponent(name)}`))
    );
    const merged = dedupeBy(
      responses.flatMap(resp => resp.data.results || []),
      r => r.serial_number
    );
    const timestamps = responses
      .map(resp => resp.data.last_updated)
      .filter(Boolean)
      .sort();
    return {
      results: merged,
      lastUpdated: timestamps.length > 0 ? timestamps[timestamps.length - 1] : null
    };
  }, []);

  const fetchScanStatus = useCallback(async () => {
    try {
      const response = await axios.get('/api/onboarding/scan-status');
      setScanStatus(response.data);
    } catch (err) {
      console.error('Failed to load scan status:', err);
    }
  }, []);

  // Load available groups on mount (only once)
  useEffect(() => {
    if (groupsLoadedRef.current) return;
    groupsLoadedRef.current = true;

    const fetchGroups = async () => {
      setLoadingGroups(true);
      try {
        const response = await axios.get('/api/device-groups');
        setState(prev => ({ ...prev, availableGroups: response.data.groups || [] }));
      } catch (err) {
        console.error('Failed to load groups:', err);
      } finally {
        setLoadingGroups(false);
      }
    };
    fetchGroups();
  }, [setState]);

  // Load group devices and stored results when the selected group changes
  useEffect(() => {
    const loadGroupData = async () => {
      if (loadingGroupRef.current) return;
      if (lastLoadedGroupRef.current === selectedGroup) return;

      if (selectedGroup === 'local') {
        setGroupDevices([]);
        setState(prev => ({ ...prev, results: [], lastUpdated: null }));
        lastLoadedGroupRef.current = 'local';
        return;
      }

      // Wait until groups are loaded before attempting aggregation
      if (selectedGroup === 'all-groups' && availableGroups.length === 0) return;

      loadingGroupRef.current = true;
      setLoading(true);
      try {
        const groupNames = selectedGroup === 'all-groups'
          ? availableGroups.map(g => g.name)
          : [selectedGroup];

        const [deviceResponses, resultsPayload] = await Promise.all([
          Promise.all(
            groupNames.map(name => axios.get(`/api/device-groups/${encodeURIComponent(name)}`))
          ),
          fetchResults(groupNames)
        ]);

        setGroupDevices(dedupeBy(
          deviceResponses.flatMap(resp => resp.data.devices || []),
          d => d.serial
        ));
        setState(prev => ({
          ...prev,
          results: resultsPayload.results,
          lastUpdated: resultsPayload.lastUpdated,
          error: null
        }));
        lastLoadedGroupRef.current = selectedGroup;
      } catch (err) {
        console.error('Failed to load group data:', err);
        setGroupDevices([]);
        setState(prev => ({ ...prev, results: [], lastUpdated: null }));
      } finally {
        loadingGroupRef.current = false;
        setLoading(false);
      }
    };
    loadGroupData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedGroup, availableGroups, setState, fetchResults]);

  // Re-read the results the scanner produced. Status updates arrive here, not from
  // a browser-driven scan, so nothing is lost while the page is closed.
  const refreshResults = useCallback(async () => {
    if (selectedGroupNames.length === 0) return;
    try {
      const payload = await fetchResults(selectedGroupNames);
      setState(prev => ({
        ...prev,
        results: payload.results,
        lastUpdated: payload.lastUpdated
      }));
    } catch (err) {
      console.error('Failed to refresh onboarding results:', err);
    }
  }, [selectedGroupNames, fetchResults, setState]);

  const liveRuns = useMemo(() => scanStatus?.live_runs || [], [scanStatus]);
  // Drives the faster poll and the refresh-on-completion below, so it has to mean "any
  // pass is alive" rather than "the newest row is alive" — otherwise a colleague's scan
  // finishing would stop us watching our own.
  const scanRunning = liveRuns.length > 0 || Boolean(scanStatus?.last_run?.running);

  // Scanner state: polled every few seconds while a pass runs so the progress actually
  // moves on screen, and only every POLL_INTERVAL_MS when the scanner is idle.
  useEffect(() => {
    fetchScanStatus();
    const intervalId = setInterval(
      fetchScanStatus,
      scanRunning ? ACTIVE_POLL_INTERVAL_MS : POLL_INTERVAL_MS
    );
    return () => clearInterval(intervalId);
  }, [fetchScanStatus, scanRunning]);

  // Table rows: written by the backend, so re-read them on the slow cadence regardless
  // of what the scanner is doing — otherwise the page would show stale rows.
  useEffect(() => {
    const intervalId = setInterval(refreshResults, POLL_INTERVAL_MS);
    return () => clearInterval(intervalId);
  }, [refreshResults]);

  // A pass just ended — pull its results in now instead of waiting up to 30s for them.
  const wasScanningRef = useRef(false);
  useEffect(() => {
    if (wasScanningRef.current && !scanRunning) refreshResults();
    wasScanningRef.current = scanRunning;
  }, [scanRunning, refreshResults]);

  // Close the scan menu on an outside click
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (scanMenuRef.current && !scanMenuRef.current.contains(event.target)) {
        setScanMenuOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Ask the backend to start a scan pass now instead of waiting for the next
  // interval. Returns immediately — results show up on the following poll.
  // scope: 'incomplete' (devices not onboarded yet) or 'all' (every device).
  //
  // Scoped to the fleet currently picked above: these actions live under the fleet
  // selector, so acting on every fleet in the database was never what was being pointed
  // at. 'all-groups' is the one selection that does mean everything.
  const handleScanNow = async (scope) => {
    setScanMenuOpen(false);
    setScanRequested(true);
    setError(null);
    try {
      // The endpoint opens the progress row before answering, so its payload already
      // describes the running pass — show it without waiting for the next poll.
      const response = await axios.post('/api/onboarding/scan', {
        scope,
        fleet: selectedGroup,
      });
      if (response.data?.last_run) {
        setScanStatus(prev => ({ ...(prev || {}), last_run: response.data.last_run }));
      }
      await fetchScanStatus();
    } catch (err) {
      if (err.response?.status === 409) {
        setError('A scan is already running.');
      } else {
        setError(err.response?.data?.error || err.message);
      }
    } finally {
      setScanRequested(false);
    }
  };

  // Manual override for a single device: the endpoint refreshes it from FTCloud/IoT
  // and persists the result, so we just drop the response into the table.
  const handleRetryDevice = async (serialNumber) => {
    setRetryingDevice(serialNumber);
    try {
      const response = await axios.post('/api/onboarding/device-status', { serial: serialNumber });
      const result = response.data;
      setResults(prev => prev.map(r => (
        r.serial_number === serialNumber ? { ...r, ...result } : r
      )));
      updateState({ lastUpdated: new Date().toISOString() });
    } catch (err) {
      console.error(`Failed to refresh ${serialNumber}:`, err);
      setError(err.response?.data?.error || err.message);
    } finally {
      setRetryingDevice(null);
    }
  };

  const handleSort = (key) => {
    setSortConfig(prev => ({
      key,
      direction: prev.key === key && prev.direction === 'asc' ? 'desc' : 'asc'
    }));
  };

  const getFilteredAndSortedResults = () => {
    let filtered = results.filter(r => {
      // Serial number filter
      if (filters.serialNumber && !r.serial_number.toLowerCase().includes(filters.serialNumber.toLowerCase())) {
        return false;
      }
      // Azure status filter
      if (filters.azureStatus !== 'all' && r.azure_status !== filters.azureStatus) {
        return false;
      }
      // Onboarding status filter
      if (filters.completionStatus !== 'all' && normalizeStatus(r) !== filters.completionStatus) {
        return false;
      }
      // CAN Status filter — compares against the normalized complete/no_data values
      if (filters.busActive !== 'all') {
        const canValue = isCanHealthy(r)
          ? CAN_HEALTHY
          : (r.bus_active ? CAN_NO_DATA : null);
        if (canValue !== filters.busActive) {
          return false;
        }
      }
      // Bus Speed filter
      if (filters.busSpeed !== 'all' && r.bus_speed !== filters.busSpeed) {
        return false;
      }
      // Image Version filter
      if (filters.imageVersion !== 'all' && typeof r.image_version === 'string' && r.image_version !== filters.imageVersion) {
        return false;
      }
      return true;
    });

    // Apply sorting
    if (sortConfig.key) {
      filtered.sort((a, b) => {
        // Status sorts by severity order (onboarded first), not alphabetically.
        if (sortConfig.key === 'status') {
          const diff = STATUS_ORDER.indexOf(normalizeStatus(a)) - STATUS_ORDER.indexOf(normalizeStatus(b));
          return sortConfig.direction === 'asc' ? diff : -diff;
        }

        let aVal = a[sortConfig.key];
        let bVal = b[sortConfig.key];

        // Handle null values
        if (aVal === null || aVal === undefined) aVal = '';
        if (bVal === null || bVal === undefined) bVal = '';
        
        if (typeof aVal === 'string') {
          aVal = aVal.toLowerCase();
          bVal = bVal.toLowerCase();
        }
        
        if (aVal < bVal) return sortConfig.direction === 'asc' ? -1 : 1;
        if (aVal > bVal) return sortConfig.direction === 'asc' ? 1 : -1;
        return 0;
      });
    } else {
      // Default order: onboarded devices first, then the ones still needing attention.
      filtered.sort((a, b) => {
        const diff = STATUS_ORDER.indexOf(normalizeStatus(a)) - STATUS_ORDER.indexOf(normalizeStatus(b));
        return diff !== 0 ? diff : a.serial_number.localeCompare(b.serial_number);
      });
    }

    return filtered;
  };

  // Check exactly the rows the filters are showing, so a filter stops being just a view
  // and becomes a selection you can act on. The pass runs server-side like any other, so
  // the progress row and the refresh-on-completion come for free — and unlike the
  // scope-based scans, an explicit selection re-checks devices that are already
  // onboarded, since asking for them by serial is the request to look again.
  const handleCheckFiltered = async () => {
    const serials = getFilteredAndSortedResults()
      .map(r => r.serial_number)
      .filter(Boolean);
    if (serials.length === 0) return;

    setScanRequested(true);
    setError(null);
    try {
      const response = await axios.post('/api/onboarding/scan-serials', { serials });
      if (response.data?.last_run) {
        setScanStatus(prev => ({ ...(prev || {}), last_run: response.data.last_run }));
      }
      await fetchScanStatus();
    } catch (err) {
      if (err.response?.status === 409) {
        setError('A scan is already running.');
      } else {
        setError(err.response?.data?.error || err.message);
      }
    } finally {
      setScanRequested(false);
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'found':
        return <CheckCircle className="w-4 h-4 text-green-500" />;
      case 'not_found':
        return <XCircle className="w-4 h-4 text-red-500" />;
      case 'error':
        return <AlertTriangle className="w-4 h-4 text-yellow-500" />;
      default:
        return <AlertTriangle className="w-4 h-4 text-gray-400" />;
    }
  };

  const getStatusBadge = (status) => {
    const styles = {
      found: 'bg-green-100 text-green-800',
      not_found: 'bg-red-100 text-red-800',
      error: 'bg-yellow-100 text-yellow-800',
      unknown: 'bg-gray-100 text-gray-800'
    };
    const labels = {
      found: 'Registered',
      not_found: 'Not Found',
      error: 'Error',
      unknown: 'Unknown'
    };
    return (
      <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${styles[status] || styles.unknown}`}>
        {labels[status] || status}
      </span>
    );
  };

  const getValueCell = (value) => {
    if (value === null || value === undefined) {
      return <span className="text-gray-400 italic">N/A</span>;
    }
    return <span className="font-mono text-sm">{String(value)}</span>;
  };

  const canStatusLabel = (result) => {
    if (isCanHealthy(result)) return CAN_LABELS[CAN_HEALTHY];
    if (!result.bus_active) return '';
    return CAN_LABELS[String(result.bus_active).toLowerCase()] || String(result.bus_active);
  };

  const getCanStatusCell = (result) => {
    const label = canStatusLabel(result);
    if (!label) {
      return <span className="text-gray-400 italic">N/A</span>;
    }
    return (
      <Badge
        variant="outline"
        className={isCanHealthy(result)
          ? 'bg-green-50 text-green-700 border-green-300 text-xs'
          : 'bg-orange-50 text-orange-700 border-orange-300 text-xs'}
      >
        {label}
      </Badge>
    );
  };

  // Signals the device reported after installation (vehicleBusStatus.
  // hasReceivedAfterInstallation) — advanced view only. Shows the count; the full list
  // opens in a modal, since a device can report 25+ signals.
  const getCanSignalsCell = (result) => {
    const signals = result.can_signals_received;
    if (!Array.isArray(signals) || signals.length === 0) {
      return <span className="text-gray-400 italic">N/A</span>;
    }
    return (
      <button
        type="button"
        onClick={() => setCanSignalsModal({ serial: result.serial_number, signals })}
        className="text-xs font-medium text-blue-700 hover:text-blue-900 hover:underline whitespace-nowrap"
        title="Show every signal received after installation"
      >
        {signals.length} {signals.length === 1 ? 'signal' : 'signals'}
      </button>
    );
  };

  // Decode a VIN on demand and show the truck details in a modal. Uses the same
  // /api/vin/decode endpoint as the standalone VIN Decoder card.
  const openVinModal = async (result) => {
    const vin = result.vin;
    if (!vin) return;
    setVinModal({ serial: result.serial_number, vin, loading: true, error: null, data: null });
    try {
      const response = await axios.get('/api/vin/decode', { params: { vin } });
      setVinModal(prev => (
        prev && prev.vin === vin
          ? { ...prev, loading: false, data: response.data }
          : prev
      ));
    } catch (err) {
      const message = err.response?.data?.error || err.message;
      setVinModal(prev => (
        prev && prev.vin === vin ? { ...prev, loading: false, error: message } : prev
      ));
    }
  };

  const getVinCell = (result) => {
    if (!result.vin) {
      return <span className="text-gray-400 italic">N/A</span>;
    }
    return (
      <button
        type="button"
        onClick={() => openVinModal(result)}
        className="font-mono text-xs text-blue-700 hover:text-blue-900 hover:underline"
        title="Decode this VIN"
      >
        {result.vin}
      </button>
    );
  };

  const SortableHeader = ({ label, sortKey }) => (
    <th
      className="text-left cursor-pointer hover:bg-gray-200 select-none whitespace-nowrap px-3 py-2"
      onClick={() => handleSort(sortKey)}
    >
      <div className="flex items-center gap-1">
        {label}
        <ArrowUpDown className="w-3 h-3 text-gray-400" />
        {sortConfig.key === sortKey && (
          sortConfig.direction === 'asc'
            ? <ChevronUp className="w-3 h-3" />
            : <ChevronDown className="w-3 h-3" />
        )}
      </div>
    </th>
  );

  // Exports the rows currently filtered and sorted on screen, as a real worksheet.
  //
  // This used to write CSV with every cell wrapped in double quotes. Excel re-typed the
  // numeric-looking serials anyway (0071043707 came back as 71043707), and the quotes
  // then travelled into Manage Fleets' importer, which stored serials as the literal
  // string "0071004A83". Identifier columns are pinned to text below so neither happens.
  const downloadCSV = () => {
    const data = getFilteredAndSortedResults();
    if (data.length === 0) return;

    const headers = ['Status', 'Serial Number', 'IoT Registration', 'Type', 'Video Solution', 'Upgrade Status', 'Config Status', 'LTA Version', 'Image Version', 'Base Image', 'Last Update', 'IMEI', 'ICCID', 'VIN', 'IPC Version', 'Power Pack Version', 'CAN Status', 'Bus Speed', 'CAN Signals After Install'];
    const rows = data.map(r => {
      const upgradeInfo = upgradeInfoMap[r.serial_number];
      return [
        statusMeta(normalizeStatus(r)).label,
        r.serial_number,
        r.azure_status === 'found' ? 'Registered' : r.azure_status === 'not_found' ? 'Not Found' : r.azure_status,
        upgradeInfo?.deviceType || 'N/A',
        upgradeInfo?.upgradeType || 'PSVI',
        upgradeInfo?.upgradeStatus || '',
        upgradeInfo?.configStatus || '',
        r.lta_version || 'N/A',
        r.image_version || 'N/A',
        r.base_image || 'N/A',
        r.last_update || '',
        r.imei || '',
        r.iccid || '',
        r.vin || '',
        r.ipc_version || '',
        r.power_pack_version || '',
        canStatusLabel(r),
        r.bus_speed || '',
        (r.can_signals_received || []).join(' ')
      ];
    });

    downloadSheet({
      filename: `onboarding_status_${new Date().toISOString().split('T')[0]}.xlsx`,
      sheetName: 'Onboarding Status',
      headers,
      rows,
      // Every one of these is an identifier that Excel would happily turn into a number.
      textColumns: ['Serial Number', 'IMEI', 'ICCID', 'VIN', 'LTA Version', 'Bus Speed'],
    });
  };

  const filteredResults = getFilteredAndSortedResults();

  // One count per status, keyed by the status value.
  const statusCounts = useMemo(() => {
    const counts = STATUS_ORDER.reduce((acc, status) => ({ ...acc, [status]: 0 }), {});
    results.forEach(r => {
      const status = normalizeStatus(r);
      counts[status] = (counts[status] || 0) + 1;
    });
    return counts;
  }, [results]);

  const onboardedCount = statusCounts[STATUS_ONBOARDED] || 0;
  const pendingCount = results.length - onboardedCount;


  // Pagination calculations
  const totalPages = Math.ceil(filteredResults.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  const paginatedResults = filteredResults.slice(startIndex, endIndex);
  
  // Reset to page 1 when filters change
  useEffect(() => {
    setCurrentPage(1);
  }, [filters.serialNumber, filters.azureStatus, filters.completionStatus, filters.busActive, filters.busSpeed, sortConfig]);
  
  const totalFleetDevices = useMemo(() => {
    return availableGroups.reduce((sum, group) => sum + (group.device_count || (group.devices?.length || 0)), 0);
  }, [availableGroups]);

  const fleetOptions = useMemo(() => {
    const baseOptions = [
      {
        value: 'all-groups',
        label: `All Fleets (${totalFleetDevices})`,
        description: 'Use every fleet configured in Manage Fleets'
      }
    ];
    const groupOptions = availableGroups.map(group => ({
      value: group.name,
      label: `${group.name} (${group.device_count || 0})`,
      description: 'Fleet saved in Manage Fleets'
    }));
    return [...baseOptions, ...groupOptions];
  }, [availableGroups, devices.length, totalFleetDevices]);

  const filteredFleetOptions = useMemo(() => {
    const term = fleetSearchTerm.trim().toLowerCase();
    if (!term) return fleetOptions;
    return fleetOptions.filter(option =>
      option.value.toLowerCase().includes(term) || option.label.toLowerCase().includes(term)
    );
  }, [fleetOptions, fleetSearchTerm]);

  // What the scan actions will act on, in the operator's own words.
  const scanScopeLabel = useMemo(
    () => (selectedGroup === 'all-groups' || !selectedGroup ? 'all fleets' : selectedGroup),
    [selectedGroup]
  );

  const selectedFleetOption = useMemo(() => {
    return fleetOptions.find(option => option.value === selectedGroup) || fleetOptions[0];
  }, [fleetOptions, selectedGroup]);

  const handleFleetSelect = (value) => {
    setSelectedGroup(value);
    setFleetDropdownOpen(false);
    setFleetSearchTerm('');
    if (fleetSearchInputRef.current) {
      fleetSearchInputRef.current.blur();
    }
  };

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (fleetDropdownRef.current && !fleetDropdownRef.current.contains(event.target)) {
        setFleetDropdownOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  return (
    <div className="space-y-4">
      <div className="rounded-2xl border border-slate-200 bg-gradient-to-br from-white via-slate-50 to-slate-100 p-4 lg:p-5 shadow-sm">
            <div className="flex flex-col lg:flex-row lg:items-center gap-3 flex-wrap">
              <div className="flex-1 min-w-[240px]" ref={fleetDropdownRef}>
                <div className="relative">
                  <Users className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                  <Input
                    ref={fleetSearchInputRef}
                    id="fleetSearch"
                    type="text"
                    value={fleetSearchTerm}
                    placeholder={selectedFleetOption?.label || 'Select fleet'}
                    disabled={loading || loadingGroups}
                    onFocus={() => {
                      setFleetDropdownOpen(true);
                    }}
                    onClick={() => {
                      setFleetDropdownOpen(true);
                    }}
                    onChange={(e) => {
                      setFleetSearchTerm(e.target.value);
                      setFleetDropdownOpen(true);
                    }}
                    className="pl-9 w-full bg-white border border-slate-200 focus:ring-2 focus:ring-blue-500/30"
                  />
                  {fleetDropdownOpen && (
                    <div className="absolute z-20 w-full bg-white border border-slate-200 rounded-xl shadow-xl mt-1 max-h-64 overflow-y-auto">
                      {filteredFleetOptions.length === 0 ? (
                        <p className="px-3 py-2 text-sm text-gray-500">No fleets found</p>
                      ) : (
                        filteredFleetOptions.map(option => (
                          <button
                            key={option.value}
                            type="button"
                            onMouseDown={(e) => e.preventDefault()}
                            onClick={() => handleFleetSelect(option.value)}
                            className={`w-full text-left px-3 py-2 hover:bg-slate-50 ${selectedGroup === option.value ? 'bg-blue-50 border-l-4 border-blue-400' : ''}`}
                          >
                            <p className="text-sm font-medium text-gray-900">{option.label}</p>
                            {option.description && (
                              <p className="text-xs text-gray-500">{option.description}</p>
                            )}
                          </button>
                        ))
                      )}
                    </div>
                  )}
                </div>
              </div>

              {/* Admin-only scan actions. The page refreshes itself, so these exist for
                  the cases where waiting for tonight's pass is not acceptable. */}
              {/* Disabled only by this browser's own in-flight request. Greying it out
                  because someone else is scanning was the same block removed everywhere
                  else: the backend now refuses just a duplicate of the same scope and
                  fleet, and says why — better than a dead button with no explanation. */}
              {isAdmin() && (
                <div className="relative" ref={scanMenuRef}>
                  <Button
                    variant="outline"
                    size="icon"
                    onClick={() => setScanMenuOpen(open => !open)}
                    disabled={scanRequested}
                    className="h-11 w-11 border-slate-300"
                    title="Scan actions"
                    aria-label="Scan actions"
                    aria-haspopup="menu"
                    aria-expanded={scanMenuOpen}
                  >
                    {scanRequested
                      ? <Loader2 className="w-5 h-5 animate-spin" />
                      : <MoreVertical className="w-5 h-5" />}
                  </Button>
                  {scanMenuOpen && (
                    <div
                      role="menu"
                      className="absolute right-0 z-20 mt-1 w-72 bg-white border border-slate-200 rounded-xl shadow-xl overflow-hidden"
                    >
                      <p className="px-3 pt-2 pb-1 text-xs font-medium text-gray-400 uppercase tracking-wide">
                        {scanScopeLabel}
                      </p>
                      <button
                        type="button"
                        role="menuitem"
                        onClick={() => handleScanNow('incomplete')}
                        className="w-full text-left px-3 py-2 hover:bg-slate-50"
                      >
                        <p className="text-sm font-medium text-gray-900">Scan incomplete devices</p>
                        <p className="text-xs text-gray-500">
                          Refresh every device in {scanScopeLabel} that is not onboarded yet
                        </p>
                      </button>
                      <button
                        type="button"
                        role="menuitem"
                        onClick={() => handleScanNow('all')}
                        className="w-full text-left px-3 py-2 hover:bg-slate-50 border-t border-slate-100"
                      >
                        <p className="text-sm font-medium text-gray-900">Scan all devices</p>
                        <p className="text-xs text-gray-500">
                          Revisit onboarded devices in {scanScopeLabel} too — slower
                        </p>
                      </button>
                    </div>
                  )}
                </div>
              )}
            </div>

        {/* Background scanner state — the page no longer scans on its own */}
        <div className="mt-3 flex flex-wrap items-center gap-x-4 gap-y-1 text-xs text-slate-600">
          <span className="flex items-center gap-1">
            <Activity className={`w-3 h-3 ${scanStatus?.enabled ? 'text-green-600' : 'text-gray-400'}`} />
            {scanStatus?.enabled
              ? `Auto-updating daily at ${scanStatus.scan_at_utc} UTC`
              : 'Automatic scanning is disabled'}
          </span>
          {/* A pass killed mid-flight (pod restart, deploy) is closed by the backend once
              its heartbeat stops. Saying so beats silently reverting to "next scan at…",
              since the devices it had not reached yet still hold their old status. */}
          {!scanRunning && scanStatus?.last_run?.status === 'interrupted' && (
            <span className="flex items-center gap-1 text-amber-700">
              <AlertTriangle className="w-3 h-3" />
              Last scan was interrupted after {scanStatus.last_run.scanned}
              {scanStatus.last_run.total ? ` / ${scanStatus.last_run.total}` : ''} device(s)
            </span>
          )}
          {!scanRunning && scanStatus?.next_run_at && (
            <span className="flex items-center gap-1">
              <Clock className="w-3 h-3" />
              Next scan {new Date(scanStatus.next_run_at).toLocaleString()}
            </span>
          )}
          {typeof scanStatus?.pending_devices === 'number' && (
            <span>{scanStatus.pending_devices} device(s) awaiting onboarding overall</span>
          )}
          {lastUpdated && (
            <span>Data updated {new Date(lastUpdated).toLocaleString()}</span>
          )}
        </div>

        {/* One card per live pass, side by side. The counters alone stay at 0/0 through
            the preamble, so each card's phase carries the "it is working" signal there.
            Falls back to the single bar when the backend predates live_runs. */}
        {liveRuns.length > 0
          ? <LiveScans runs={liveRuns} />
          : scanRunning && <ScanProgress lastRun={scanStatus.last_run} />}
      </div>

      {/* Error */}
      {error && (
        <div className="p-3 bg-red-50 border border-red-200 rounded-lg text-red-700 text-sm">
          {error}
        </div>
      )}

      {/* Analytics Dashboard */}
      {results.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle className="text-xl">Analytics Dashboard</CardTitle>
            <CardDescription>Device distribution and statistics overview</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
              {/* Status Distribution - Donut Chart */}
              <div className="space-y-4">
                <h3 className="text-sm font-semibold text-gray-700 border-b pb-2">Status Distribution</h3>
                <div className="flex items-center justify-center">
                  <div className="relative w-40 h-40">
                    <svg viewBox="0 0 100 100" className="transform -rotate-90">
                      {(() => {
                        const total = results.length;

                        let offset = 0;
                        const radius = 40;
                        const circumference = 2 * Math.PI * radius;

                        const segments = STATUS_ORDER.map(status => ({
                          percent: ((statusCounts[status] || 0) / total) * 100,
                          color: statusMeta(status).chartColor,
                          label: statusMeta(status).label
                        }));

                        return segments.map((seg, i) => {
                          const dashLength = (seg.percent / 100) * circumference;
                          const dashOffset = offset;
                          offset -= dashLength;
                          
                          return seg.percent > 0 ? (
                            <circle
                              key={i}
                              cx="50"
                              cy="50"
                              r={radius}
                              fill="none"
                              stroke={seg.color}
                              strokeWidth="12"
                              strokeDasharray={`${dashLength} ${circumference}`}
                              strokeDashoffset={dashOffset}
                            />
                          ) : null;
                        });
                      })()}
                    </svg>
                    <div className="absolute inset-0 flex items-center justify-center">
                      <div className="text-center">
                        <div className="text-2xl font-bold text-gray-900">{results.length}</div>
                        <div className="text-xs text-gray-500">Total</div>
                      </div>
                    </div>
                  </div>
                </div>
                <div className="space-y-1 text-sm">
                  {STATUS_ORDER.map(status => (
                    <div key={status} className="flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <div className={`w-3 h-3 rounded-full ${statusMeta(status).dotClass}`}></div>
                        <span>{statusMeta(status).label}</span>
                      </div>
                      <span className="font-medium">{statusCounts[status] || 0}</span>
                    </div>
                  ))}
                </div>
              </div>

              {/* Video Solution & Device Type - Stacked */}
              <div className="space-y-4">
                <h3 className="text-sm font-semibold text-gray-700 border-b pb-2">Type Distribution</h3>
                <div className="space-y-4">
                  {/* Video Solution */}
                  <div>
                    <div className="text-xs text-gray-600 mb-2">Video Solution</div>
                    {(() => {
                      const viCount = results.filter(r => upgradeInfoMap[r.serial_number]?.upgradeType === 'VI').length;
                      const psviCount = results.length - viCount;
                      const total = results.length;

                      return (
                        <div className="space-y-2">
                          <div className="h-8 bg-gray-100 rounded-lg overflow-hidden flex">
                            {viCount > 0 && (
                              <div
                                className="bg-gray-700 flex items-center justify-center text-white text-xs font-medium"
                                style={{ width: `${(viCount / total) * 100}%` }}
                              >
                                {viCount > 5 && `VI: ${viCount}`}
                              </div>
                            )}
                            {psviCount > 0 && (
                              <div
                                className="bg-gray-500 flex items-center justify-center text-white text-xs font-medium"
                                style={{ width: `${(psviCount / total) * 100}%` }}
                              >
                                {psviCount > 5 && `PSVI: ${psviCount}`}
                              </div>
                            )}
                          </div>
                          <div className="flex gap-3 text-xs">
                            <span><span className="font-medium">VI:</span> {viCount}</span>
                            <span><span className="font-medium">PSVI:</span> {psviCount}</span>
                          </div>
                        </div>
                      );
                    })()}
                  </div>

                  {/* CAN Status */}
                  <div>
                    <div className="text-xs text-gray-600 mb-2">CAN Status</div>
                    {(() => {
                      // "Complete" means the device reported signals after installation
                      // (vehicleBusStatus.hasReceivedAfterInstallation), not busActive.
                      const healthy = results.filter(isCanHealthy).length;
                      const noData = results.filter(r => !isCanHealthy(r) && r.bus_active).length;
                      const unknown = results.length - healthy - noData;
                      const total = results.length;

                      return (
                        <div className="space-y-2">
                          <div className="h-8 bg-gray-100 rounded-lg overflow-hidden flex">
                            {healthy > 0 && (
                              <div
                                className="bg-gray-700 flex items-center justify-center text-white text-xs font-medium"
                                style={{ width: `${(healthy / total) * 100}%` }}
                              >
                                {healthy > 5 && `Healthy: ${healthy}`}
                              </div>
                            )}
                            {noData > 0 && (
                              <div
                                className="bg-gray-500 flex items-center justify-center text-white text-xs font-medium"
                                style={{ width: `${(noData / total) * 100}%` }}
                              >
                                {noData > 5 && `No Data: ${noData}`}
                              </div>
                            )}
                            {unknown > 0 && (
                              <div
                                className="bg-gray-300 flex items-center justify-center text-gray-700 text-xs font-medium"
                                style={{ width: `${(unknown / total) * 100}%` }}
                              >
                                {unknown > 5 && `Unknown: ${unknown}`}
                              </div>
                            )}
                          </div>
                          <div className="flex gap-3 text-xs">
                            <span><span className="font-medium">Healthy:</span> {healthy}</span>
                            <span><span className="font-medium">No Data:</span> {noData}</span>
                            {unknown > 0 && <span><span className="font-medium">Unknown:</span> {unknown}</span>}
                          </div>
                        </div>
                      );
                    })()}
                  </div>

                  {/* Bus Speed */}
                  <div>
                    <div className="text-xs text-gray-600 mb-2">Bus Speed</div>
                    {(() => {
                      const speed500k = results.filter(r => r.bus_speed === '500k').length;
                      const speed250k = results.filter(r => r.bus_speed === '250k').length;
                      const total = speed500k + speed250k; // Only count 500k and 250k devices
                      
                      return (
                        <div className="space-y-2">
                          <div className="h-8 bg-gray-100 rounded-lg overflow-hidden flex">
                            {speed500k > 0 && total > 0 && (
                              <div 
                                className="bg-gray-700 flex items-center justify-center text-white text-xs font-medium"
                                style={{ width: `${(speed500k / total) * 100}%` }}
                              >
                                {speed500k > 5 && `500k: ${speed500k}`}
                              </div>
                            )}
                            {speed250k > 0 && total > 0 && (
                              <div 
                                className="bg-gray-500 flex items-center justify-center text-white text-xs font-medium"
                                style={{ width: `${(speed250k / total) * 100}%` }}
                              >
                                {speed250k > 5 && `250k: ${speed250k}`}
                              </div>
                            )}
                          </div>
                          <div className="flex gap-3 text-xs">
                            <span><span className="font-medium">500k:</span> {speed500k}</span>
                            <span><span className="font-medium">250k:</span> {speed250k}</span>
                            {total > 0 && <span className="text-gray-500">({total} total)</span>}
                          </div>
                        </div>
                      );
                    })()}
                  </div>
                </div>
              </div>

              {/* LTA Version Distribution - Simple List */}
              <div className="space-y-4">
                <h3 className="text-sm font-semibold text-gray-700 border-b pb-2">LTA Versions</h3>
                <div className="space-y-2">
                  {(() => {
                    const versionCounts = {};
                    results.forEach(r => {
                      const version = r.lta_version || 'Unknown';
                      versionCounts[version] = (versionCounts[version] || 0) + 1;
                    });
                    
                    // Separate Unknown from other versions
                    const unknownCount = versionCounts['Unknown'] || 0;
                    delete versionCounts['Unknown'];
                    
                    // Sort other versions by count
                    const sortedVersions = Object.entries(versionCounts)
                      .sort(([, a], [, b]) => b - a)
                      .slice(0, 7);
                    
                    // Add Unknown at the end if it exists
                    if (unknownCount > 0) {
                      sortedVersions.push(['Unknown', unknownCount]);
                    }
                    
                    const total = results.length;
                    
                    return sortedVersions.map(([version, count], idx) => {
                      const percentage = total > 0 ? ((count / total) * 100).toFixed(1) : 0;
                      const isUnknown = version === 'Unknown';
                      
                      return (
                        <div key={version} className={`flex items-center justify-between text-sm py-1 border-b border-gray-100 last:border-0 ${isUnknown ? 'text-gray-400' : ''}`}>
                          <div className="flex items-center gap-2">
                            <span className="text-xs font-medium text-gray-400 w-4">{isUnknown ? '-' : idx + 1}</span>
                            <span className="font-mono text-xs">{version}</span>
                          </div>
                          <div className="flex items-center gap-2">
                            <span className="text-xs text-gray-500">{percentage}%</span>
                            <span className="font-medium">{count}</span>
                          </div>
                        </div>
                      );
                    });
                  })()}
                </div>

                {/* Device Type */}
                <div className="mt-6 pt-4 border-t">
                  <h3 className="text-xs text-gray-600 mb-2">Device Type</h3>
                  {(() => {
                    const m1nCount = results.filter(r => {
                      const deviceType = upgradeInfoMap[r.serial_number]?.deviceType;
                      return deviceType === 'M1N' || deviceType === 'm1n';
                    }).length;
                    const dashcamCount = results.filter(r => {
                      const deviceType = upgradeInfoMap[r.serial_number]?.deviceType;
                      return deviceType === 'DASHCAM' || deviceType === 'dashcam' || deviceType === 'Dashcam';
                    }).length;
                    const unknownCount = results.filter(r => {
                      const deviceType = upgradeInfoMap[r.serial_number]?.deviceType;
                      return !deviceType || (deviceType !== 'M1N' && deviceType !== 'm1n' && deviceType !== 'DASHCAM' && deviceType !== 'dashcam' && deviceType !== 'Dashcam');
                    }).length;
                    const total = results.length;
                    
                    return (
                      <div className="space-y-2">
                        <div className="h-8 bg-gray-100 rounded-lg overflow-hidden flex">
                          {m1nCount > 0 && (
                            <div 
                              className="bg-gray-700 flex items-center justify-center text-white text-xs font-medium"
                              style={{ width: `${(m1nCount / total) * 100}%` }}
                            >
                              {m1nCount > 5 && `M1N: ${m1nCount}`}
                            </div>
                          )}
                          {dashcamCount > 0 && (
                            <div 
                              className="bg-gray-500 flex items-center justify-center text-white text-xs font-medium"
                              style={{ width: `${(dashcamCount / total) * 100}%` }}
                            >
                              {dashcamCount > 5 && `DASHCAM: ${dashcamCount}`}
                            </div>
                          )}
                          {unknownCount > 0 && (
                            <div 
                              className="bg-gray-300 flex items-center justify-center text-gray-700 text-xs font-medium"
                              style={{ width: `${(unknownCount / total) * 100}%` }}
                            >
                              {unknownCount > 5 && `?: ${unknownCount}`}
                            </div>
                          )}
                        </div>
                        <div className="flex gap-3 text-xs">
                          <span><span className="font-medium">M1N:</span> {m1nCount}</span>
                          <span><span className="font-medium">DASHCAM:</span> {dashcamCount}</span>
                          {unknownCount > 0 && <span><span className="font-medium">Unknown:</span> {unknownCount}</span>}
                        </div>
                      </div>
                    );
                  })()}
                </div>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Results */}
      {results.length > 0 && (
        <Card>
          <CardHeader>
            <div className="flex items-center justify-between">
              <div>
                <CardTitle>Results</CardTitle>
                <CardDescription>
                  <span className="text-green-600 font-medium">{onboardedCount} onboarded</span>
                  {' · '}
                  <span className="text-orange-600 font-medium">{pendingCount} pending</span>
                  {filteredResults.length !== results.length && (
                    <span className="text-gray-500"> · Showing {filteredResults.length} of {results.length}</span>
                  )}
                </CardDescription>
              </div>
              <div className="flex items-center gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setCompactView(!compactView)}
                  className="flex items-center gap-1"
                  title={compactView ? "Advanced view" : "Compact view"}
                >
                  {compactView ? <Maximize2 className="w-4 h-4" /> : <Minimize2 className="w-4 h-4" />}
                  {compactView ? "Advanced" : "Compact"}
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setShowFilters(!showFilters)}
                  className="flex items-center gap-1"
                >
                  <Filter className="w-4 h-4" />
                  Filters
                  {showFilters ? <ChevronUp className="w-3 h-3" /> : <ChevronDown className="w-3 h-3" />}
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setShowColumnMenu(!showColumnMenu)}
                  className="flex items-center gap-1"
                >
                  <Columns className="w-4 h-4" />
                  Columns
                  {showColumnMenu ? <ChevronUp className="w-3 h-3" /> : <ChevronDown className="w-3 h-3" />}
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={downloadCSV}
                  className="flex items-center gap-1"
                >
                  <Download className="w-4 h-4" />
                  Export Excel
                </Button>
              </div>
            </div>
          </CardHeader>
          <CardContent>
            {/* Filters Panel */}
            {showFilters && (
              <div className="mb-4 p-4 bg-gray-50 rounded-lg border space-y-3">
                <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
                  <div className="space-y-1">
                    <Label className="text-xs">Serial Number</Label>
                    <Input
                      placeholder="Filter by serial..."
                      value={filters.serialNumber}
                      onChange={(e) => setFilters(prev => ({ ...prev, serialNumber: e.target.value }))}
                      className="h-8 text-sm"
                    />
                  </div>
                  <div className="space-y-1">
                    <Label className="text-xs">IoT Registration</Label>
                    <select
                      value={filters.azureStatus}
                      onChange={(e) => setFilters(prev => ({ ...prev, azureStatus: e.target.value }))}
                      className="flex h-8 w-full rounded-md border border-input bg-background px-2 py-1 text-sm"
                    >
                      <option value="all">All</option>
                      <option value="found">Registered</option>
                      <option value="not_found">Not Found</option>
                      <option value="error">Error</option>
                    </select>
                  </div>
                  <div className="space-y-1">
                    <Label className="text-xs">Status</Label>
                    <select
                      value={filters.completionStatus}
                      onChange={(e) => setFilters(prev => ({ ...prev, completionStatus: e.target.value }))}
                      className="flex h-8 w-full rounded-md border border-input bg-background px-2 py-1 text-sm"
                    >
                      <option value="all">All</option>
                      {STATUS_ORDER.map(status => (
                        <option key={status} value={status}>{statusMeta(status).label}</option>
                      ))}
                    </select>
                  </div>
                  <div className="space-y-1">
                    <Label className="text-xs">CAN Status</Label>
                    <select
                      value={filters.busActive}
                      onChange={(e) => setFilters(prev => ({ ...prev, busActive: e.target.value }))}
                      className="flex h-8 w-full rounded-md border border-input bg-background px-2 py-1 text-sm"
                    >
                      <option value="all">All</option>
                      <option value={CAN_HEALTHY}>Healthy</option>
                      <option value={CAN_NO_DATA}>No Data</option>
                    </select>
                  </div>
                  <div className="space-y-1">
                    <Label className="text-xs">Bus Speed</Label>
                    <select
                      value={filters.busSpeed}
                      onChange={(e) => setFilters(prev => ({ ...prev, busSpeed: e.target.value }))}
                      className="flex h-8 w-full rounded-md border border-input bg-background px-2 py-1 text-sm"
                    >
                      <option value="all">All</option>
                      {/* Get unique bus speeds from results */}
                      {[...new Set(results.map(r => r.bus_speed).filter(Boolean))].sort().map(speed => (
                        <option key={speed} value={speed}>{speed}</option>
                      ))}
                    </select>
                  </div>
                  <div className="space-y-1">
                    <Label className="text-xs">Image Version</Label>
                    <select
                      value={filters.imageVersion}
                      onChange={(e) => setFilters(prev => ({ ...prev, imageVersion: e.target.value }))}
                      className="flex h-8 w-full rounded-md border border-input bg-background px-2 py-1 text-sm"
                    >
                      <option value="all">All</option>
                      {/* Get unique image versions from results - filter out non-string values */}
                      {[...new Set(results.map(r => r.image_version).filter(v => v && typeof v === 'string'))].sort().map(version => (
                        <option key={version} value={version}>{version}</option>
                      ))}
                    </select>
                  </div>
                </div>
                <div className="flex items-center justify-between pt-2 border-t">
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => setFilters({ serialNumber: '', azureStatus: 'all', completionStatus: 'all', busActive: 'all', busSpeed: 'all', imageVersion: 'all' })}
                    className="text-xs"
                  >
                    Clear Filters
                  </Button>
                  {/* Re-check the filtered rows now. Viewers can filter and export but
                      not spend FTCloud/IoT calls, which is why this is edit-gated. */}
                  {!isViewer() && (
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={handleCheckFiltered}
                      disabled={scanRequested || filteredResults.length === 0}
                      className="flex items-center gap-1 text-xs"
                      title="Refresh these devices from FTCloud and IoT now"
                    >
                      {scanRequested
                        ? <Loader2 className="w-4 h-4 animate-spin" />
                        : <RefreshCw className="w-4 h-4" />}
                      Check {filteredResults.length} device{filteredResults.length === 1 ? '' : 's'}
                    </Button>
                  )}
                </div>
              </div>
            )}

            {/* Column Visibility Menu */}
            {showColumnMenu && (
              <div className="mb-4 p-4 bg-blue-50 rounded-lg border border-blue-200">
                <h4 className="text-sm font-medium mb-3 text-blue-900">Show/Hide Columns</h4>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                  {[
                    { key: 'status', label: 'Status' },
                    { key: 'serialNumber', label: 'Serial Number' },
                    { key: 'azureStatus', label: 'IoT Registration' },
                    { key: 'deviceType', label: 'Device Type' },
                    { key: 'upgradeType', label: 'Video Solution' },
                    { key: 'upgradeStatus', label: 'Upgrade Status' },
                    { key: 'configStatus', label: 'Config Status' },
                    { key: 'ltaVersion', label: 'LTA Version' },
                    { key: 'imageVersion', label: 'Image Version' },
                    { key: 'baseImage', label: 'Base Image' },
                    { key: 'lastUpdate', label: 'Last Update' },
                    { key: 'imei', label: 'IMEI' },
                    { key: 'iccid', label: 'ICCID' },
                    { key: 'vin', label: 'VIN' },
                    { key: 'ipcVersion', label: 'IPC Version' },
                    { key: 'powerPackVersion', label: 'Power Pack Version' },
                    { key: 'busActive', label: 'CAN Status' },
                    { key: 'busSpeed', label: 'Bus Speed' },
                    { key: 'canSignals', label: 'CAN Signals (advanced)' }
                  ].map(col => (
                    <label key={col.key} className="flex items-center gap-2 cursor-pointer hover:bg-blue-100 p-2 rounded">
                      <input
                        type="checkbox"
                        checked={visibleColumns[col.key]}
                        onChange={() => toggleColumn(col.key)}
                        className="h-4 w-4 rounded border-gray-300"
                      />
                      <span className="text-sm flex items-center gap-1">
                        {visibleColumns[col.key] ? <Eye className="w-3 h-3 text-blue-600" /> : <EyeOff className="w-3 h-3 text-gray-400" />}
                        {col.label}
                      </span>
                    </label>
                  ))}
                </div>
              </div>
            )}

            {/* Table */}
            <div className="border rounded-lg overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-gray-100">
                  <tr>
                    {effectiveColumns.status && <SortableHeader label="Status" sortKey="status" />}
                    {effectiveColumns.serialNumber && <SortableHeader label="Serial Number" sortKey="serial_number" />}
                    {effectiveColumns.azureStatus && <SortableHeader label="IoT Registration" sortKey="azure_status" />}
                    {effectiveColumns.deviceType && <th className="px-3 py-2 text-left">Device Type</th>}
                    {effectiveColumns.upgradeType && <th className="px-3 py-2 text-left">Video Solution</th>}
                    {effectiveColumns.upgradeStatus && <th className="px-3 py-2 text-left">Upgrade Status</th>}
                    {effectiveColumns.configStatus && <th className="px-3 py-2 text-left">Config Status</th>}
                    {effectiveColumns.ltaVersion && <SortableHeader label="LTA Version" sortKey="lta_version" />}
                    {effectiveColumns.imageVersion && <SortableHeader label="Image Version" sortKey="image_version" />}
                    {effectiveColumns.baseImage && <SortableHeader label="Base Image" sortKey="base_image" />}
                    {effectiveColumns.lastUpdate && <SortableHeader label="Last Update" sortKey="last_update" />}
                    {effectiveColumns.imei && <SortableHeader label="IMEI" sortKey="imei" />}
                    {effectiveColumns.iccid && <SortableHeader label="ICCID" sortKey="iccid" />}
                    {effectiveColumns.vin && <SortableHeader label="VIN" sortKey="vin" />}
                    {effectiveColumns.ipcVersion && <SortableHeader label="IPC Version" sortKey="ipc_version" />}
                    {effectiveColumns.powerPackVersion && <SortableHeader label="Power Pack" sortKey="power_pack_version" />}
                    {effectiveColumns.busActive && <SortableHeader label="CAN Status" sortKey="bus_active" />}
                    {effectiveColumns.busSpeed && <SortableHeader label="Bus Speed" sortKey="bus_speed" />}
                    {effectiveColumns.canSignals && <th className="px-3 py-2 text-left">CAN Signals</th>}
                    <th className="px-3 py-2 text-left">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {paginatedResults.map((result, idx) => {
                    const isRetrying = retryingDevice === result.serial_number;
                    const status = normalizeStatus(result);
                    const meta = statusMeta(status);
                    return (
                      <tr
                        key={`${result.serial_number}-${idx}`}
                        className={`border-t hover:bg-gray-50 ${meta.rowClass}`}
                      >
                        {effectiveColumns.status && <td className="px-3 py-2">
                          <Badge variant="outline" className={`${meta.badgeClass} whitespace-nowrap`}>
                            {meta.label}
                          </Badge>
                        </td>}
                        {effectiveColumns.serialNumber && <td className="px-3 py-2 font-mono font-medium">{result.serial_number}</td>}
                        {effectiveColumns.azureStatus && <td className="px-3 py-2">
                          <div className="flex items-center gap-1">
                            {getStatusIcon(result.azure_status)}
                            {getStatusBadge(result.azure_status)}
                          </div>
                        </td>}
                        {effectiveColumns.deviceType && <td className="px-3 py-2">
                          {upgradeInfoMap[result.serial_number]?.deviceType ? (
                            <Badge 
                              variant="outline" 
                              className={upgradeInfoMap[result.serial_number].deviceType === 'M1N' 
                                ? 'bg-blue-50 text-blue-700 border-blue-300 text-xs' 
                                : 'bg-purple-50 text-purple-700 border-purple-300 text-xs'
                              }
                            >
                              {upgradeInfoMap[result.serial_number].deviceType === 'M1N' ? (
                                <Cpu className="w-3 h-3 mr-1 inline" />
                              ) : (
                                <Camera className="w-3 h-3 mr-1 inline" />
                              )}
                              {upgradeInfoMap[result.serial_number].deviceType}
                            </Badge>
                          ) : (
                            <span className="text-xs text-gray-400">-</span>
                          )}
                        </td>}
                        {effectiveColumns.upgradeType && <td className="px-3 py-2">
                          <Badge
                            variant="outline"
                            className={upgradeInfoMap[result.serial_number]?.upgradeType === 'VI'
                              ? 'bg-orange-50 text-orange-700 border-orange-300 text-xs'
                              : 'bg-teal-50 text-teal-700 border-teal-300 text-xs'
                            }
                          >
                            {upgradeInfoMap[result.serial_number]?.upgradeType || 'PSVI'}
                          </Badge>
                        </td>}
                        {effectiveColumns.upgradeStatus && <td className="px-3 py-2">
                          {upgradeInfoMap[result.serial_number]?.upgradeStatus ? (
                            <Badge variant="outline" className={getUpgradeStatusBadgeClass(upgradeInfoMap[result.serial_number].upgradeStatus)}>
                              {upgradeInfoMap[result.serial_number].upgradeStatus}
                            </Badge>
                          ) : (
                            <span className="text-xs text-gray-400">-</span>
                          )}
                        </td>}
                        {effectiveColumns.configStatus && <td className="px-3 py-2">
                          {upgradeInfoMap[result.serial_number]?.configStatus ? (
                            <Badge variant="outline" className={getUpgradeStatusBadgeClass(upgradeInfoMap[result.serial_number].configStatus)}>
                              {upgradeInfoMap[result.serial_number].configStatus}
                            </Badge>
                          ) : (
                            <span className="text-xs text-gray-400">-</span>
                          )}
                        </td>}
                        {effectiveColumns.ltaVersion && <td className="px-3 py-2">{getValueCell(result.lta_version)}</td>}
                        {effectiveColumns.imageVersion && <td className="px-3 py-2">{getValueCell(result.image_version)}</td>}
                        {effectiveColumns.baseImage && <td className="px-3 py-2" title={result.base_image || ''}>{getValueCell(result.base_image)}</td>}
                        {effectiveColumns.lastUpdate && <td className="px-3 py-2 whitespace-nowrap">{getValueCell(result.last_update)}</td>}
                        {effectiveColumns.imei && <td className="px-3 py-2 font-mono text-xs">{getValueCell(result.imei)}</td>}
                        {effectiveColumns.iccid && <td className="px-3 py-2 font-mono text-xs">{getValueCell(result.iccid)}</td>}
                        {effectiveColumns.vin && <td className="px-3 py-2">{getVinCell(result)}</td>}
                        {effectiveColumns.ipcVersion && <td className="px-3 py-2 font-mono text-xs">{getValueCell(result.ipc_version)}</td>}
                        {effectiveColumns.powerPackVersion && <td className="px-3 py-2 font-mono text-xs">{getValueCell(result.power_pack_version)}</td>}
                        {effectiveColumns.busActive && <td className="px-3 py-2">{getCanStatusCell(result)}</td>}
                        {effectiveColumns.busSpeed && <td className="px-3 py-2">{getValueCell(result.bus_speed)}</td>}
                        {effectiveColumns.canSignals && <td className="px-3 py-2">{getCanSignalsCell(result)}</td>}
                        <td className="px-3 py-2">
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleRetryDevice(result.serial_number)}
                            disabled={isViewer() || isRetrying}
                            className="h-7 px-2"
                            title={isViewer() ? 'View-only access' : 'Refresh this device now'}
                          >
                            {isRetrying ? (
                              <Loader2 className="w-3 h-3 animate-spin" />
                            ) : (
                              <RefreshCw className="w-3 h-3" />
                            )}
                            <span className="ml-1 text-xs">Refresh</span>
                          </Button>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>

            {/* Pagination Controls */}
            {totalPages > 1 && (
              <div className="flex items-center justify-between px-4 py-3 border-t">
                <div className="text-sm text-gray-600">
                  Showing {startIndex + 1} to {Math.min(endIndex, filteredResults.length)} of {filteredResults.length} results
                </div>
                <div className="flex items-center gap-2">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setCurrentPage(1)}
                    disabled={currentPage === 1}
                  >
                    First
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}
                    disabled={currentPage === 1}
                  >
                    Previous
                  </Button>
                  <div className="flex items-center gap-1">
                    {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                      let pageNum;
                      if (totalPages <= 5) {
                        pageNum = i + 1;
                      } else if (currentPage <= 3) {
                        pageNum = i + 1;
                      } else if (currentPage >= totalPages - 2) {
                        pageNum = totalPages - 4 + i;
                      } else {
                        pageNum = currentPage - 2 + i;
                      }
                      return (
                        <Button
                          key={pageNum}
                          variant={currentPage === pageNum ? "default" : "outline"}
                          size="sm"
                          onClick={() => setCurrentPage(pageNum)}
                          className="w-10"
                        >
                          {pageNum}
                        </Button>
                      );
                    })}
                  </div>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setCurrentPage(prev => Math.min(totalPages, prev + 1))}
                    disabled={currentPage === totalPages}
                  >
                    Next
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setCurrentPage(totalPages)}
                    disabled={currentPage === totalPages}
                  >
                    Last
                  </Button>
                </div>
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {/* CAN signals received after installation */}
      {canSignalsModal && (
        <div
          className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4"
          onClick={() => setCanSignalsModal(null)}
        >
          <div
            className="bg-white rounded-xl shadow-2xl w-full max-w-2xl max-h-[90vh] overflow-y-auto"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="px-6 py-4 border-b flex items-start justify-between gap-4">
              <div>
                <h2 className="text-xl font-semibold">CAN signals received</h2>
                <p className="text-sm text-muted-foreground">
                  <span className="font-mono">{canSignalsModal.serial}</span> reported{' '}
                  {canSignalsModal.signals.length} signal
                  {canSignalsModal.signals.length === 1 ? '' : 's'} after installation
                </p>
              </div>
              <Button variant="ghost" size="icon" onClick={() => setCanSignalsModal(null)}>
                <X className="w-5 h-5" />
              </Button>
            </div>
            <div className="px-6 py-4">
              <div className="flex flex-wrap gap-2">
                {[...canSignalsModal.signals].sort((a, b) => a.localeCompare(b)).map(signal => (
                  <Badge
                    key={signal}
                    variant="outline"
                    className="bg-green-50 text-green-700 border-green-300 font-mono text-xs"
                  >
                    {signal}
                  </Badge>
                ))}
              </div>
              <p className="mt-4 text-xs text-gray-500">
                From <span className="font-mono">vehicleBusStatus.hasReceivedAfterInstallation</span>
                {' '}— the signals that proved the CAN bus was working once the device was installed.
              </p>
            </div>
          </div>
        </div>
      )}

      {/* VIN decode result */}
      {vinModal && (
        <div
          className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4"
          onClick={() => setVinModal(null)}
        >
          <div
            className="bg-white rounded-xl shadow-2xl w-full max-w-3xl max-h-[90vh] overflow-y-auto"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="px-6 py-4 border-b flex items-start justify-between gap-4">
              <div>
                <h2 className="text-xl font-semibold flex items-center gap-2">
                  <Car className="w-5 h-5 text-blue-600" />
                  Vehicle details
                </h2>
                <p className="text-sm text-muted-foreground">
                  <span className="font-mono">{vinModal.serial}</span>
                  {' · VIN '}
                  <span className="font-mono">{vinModal.vin}</span>
                </p>
              </div>
              <Button variant="ghost" size="icon" onClick={() => setVinModal(null)}>
                <X className="w-5 h-5" />
              </Button>
            </div>
            <div className="px-6 py-4">
              {vinModal.loading && (
                <div className="flex items-center gap-2 text-sm text-gray-600">
                  <Loader2 className="w-4 h-4 animate-spin" />
                  Decoding VIN...
                </div>
              )}

              {vinModal.error && (
                <div className="flex items-start gap-2 p-3 bg-red-50 border border-red-200 rounded-lg text-sm text-red-700">
                  <AlertTriangle className="w-4 h-4 mt-0.5 shrink-0" />
                  {vinModal.error}
                </div>
              )}

              {vinModal.data && (() => {
                const summary = vinModal.data.summary || {};
                const present = VIN_SUMMARY_FIELDS.filter(field => summary[field.key]);
                if (present.length === 0) {
                  return (
                    <p className="text-sm text-gray-500">
                      The VIN service returned no vehicle details for this VIN.
                    </p>
                  );
                }
                return (
                  <>
                    <dl className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-2">
                      {present.map(field => (
                        <div key={field.key} className="flex justify-between gap-3 border-b border-gray-100 py-1">
                          <dt className="text-xs text-gray-500">{field.label}</dt>
                          <dd className="text-sm font-medium text-right">{summary[field.key]}</dd>
                        </div>
                      ))}
                    </dl>
                    {vinModal.data.message && (
                      <p className="mt-4 text-xs text-gray-500">{vinModal.data.message}</p>
                    )}
                  </>
                );
              })()}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default OnboardingStatus;
